"use client";
import { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../constants/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Search } from "lucide-react";
import Pagination from "../../Common/Pagination";

// Debounce function
const debounce = (func, delay) => {
  let debounceTimer;
  return function () {
    const args = arguments;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func.apply(this, args), delay);
  };
};

function ClassTeacherStudentManagement() {
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [teacherMapping, setTeacherMapping] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchTeacherMapping();
  }, []);

  const fetchTeacherMapping = async () => {
    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));

      const response = await axiosInstance.get(
        API_ENDPOINTS.classroomMappingsUser(user.id),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.data.assignment && response.data.data.assignment.assignments) {
        // Find the class teacher assignment
        const classTeacherAssignment = response.data.data.assignment.assignments.find(
          assignment => assignment.isClassTeacher
        );

        if (classTeacherAssignment) {
          setTeacherMapping(classTeacherAssignment);
          if (classTeacherAssignment.class && classTeacherAssignment.class._id) {
            fetchUnassignedStudents(classTeacherAssignment.class._id);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching teacher mapping:", error);
      setError("Failed to fetch teacher mapping");
    } finally {
      setLoading(false);
    }
  };

  const fetchUnassignedStudents = async (classId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(
        API_ENDPOINTS.classroomMappingsStudentsUnassigned(classId),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  
      if (response.data.status === "success") {
        setStudents(response.data.data.students);
      } else {
        setError("Failed to fetch unassigned students");
      }
    } catch (error) {
      console.error("Error fetching unassigned students:", error);
      setError("Failed to fetch unassigned students");
    }
  };
  

  const handleStudentSelect = (studentId) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = (checked) => {
    const studentsToSelect = filteredStudents.map((student) => student._id);
    setSelectedStudents(checked ? studentsToSelect : []);
  };

  // Search and filtering functions
  const handleSearchChange = debounce((e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length >= 2) {
      const suggestions = students
        .filter(
          (student) =>
            student.firstName?.toLowerCase().includes(query.toLowerCase()) ||
            student.lastName?.toLowerCase().includes(query.toLowerCase()) ||
            student.middleName?.toLowerCase().includes(query.toLowerCase()) ||
            (student.admissionNumber && student.admissionNumber.toString().includes(query)),
        )
        .slice(0, 5);

      setSearchSuggestions(suggestions);
      setShowSuggestions(true);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  }, 300);

  const handleSuggestionClick = (student) => {
    setSearchQuery(`${student.firstName} ${student.lastName}`);
    setSearchSuggestions([]);
    setShowSuggestions(false);
  };

  // Filter students based on search query
  const filteredStudents = students.filter(
    (student) =>
      !searchQuery ||
      student.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.middleName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getFullName(student).toLowerCase().includes(searchQuery.toLowerCase()) ||
      (student.admissionNumber && student.admissionNumber.toString().includes(searchQuery)),
  );

  const assignStudentsToSection = async () => {
    if (!selectedStudents.length) {
      setError("Please select at least one student");
      return;
    }
  
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      await axiosInstance.post(
        API_ENDPOINTS.classroomMappingsBulkAssign,
        {
          studentIds: selectedStudents,
          classId: teacherMapping.class._id,
          sectionId: teacherMapping.section._id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  
      setSuccess("Students assigned successfully");
      setSelectedStudents([]);
      fetchUnassignedStudents(teacherMapping.class._id);
  
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to assign students");
    } finally {
      setLoading(false);
    }
  };
  const getFullName = (person) => {
    if (!person) return "Unknown";
    return [person.firstName, person.middleName, person.lastName]
      .filter(Boolean)
      .join(" ");
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!teacherMapping || teacherMapping.isClassTeacher !== true) {
    return (
      <Alert>
        <AlertDescription>
          This feature is only available for class teachers.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold flex items-center justify-between">
            <span>Class Students Management</span>
            <Badge variant="secondary">
              Class: {teacherMapping.class.name}-{teacherMapping.section.name}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert className="mb-4 bg-green-50 text-green-700">
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h3 className="text-lg font-semibold">
                Unassigned Students ({filteredStudents.length})
              </h3>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                {/* Search Input */}
                <div className="relative flex-1 sm:flex-none sm:w-64">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by name or admission number..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="pl-9 pr-4 py-2 bg-white border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg"
                  />
                  {showSuggestions && searchSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                      {searchSuggestions.map((student) => (
                        <div
                          key={student._id}
                          className="p-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => handleSuggestionClick(student)}
                        >
                          {getFullName(student)}
                          {student.admissionNumber ? ` - ${student.admissionNumber}` : ""}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  onClick={assignStudentsToSection}
                  disabled={selectedStudents.length === 0 || loading}
                  className="whitespace-nowrap"
                >
                  Assign Selected Students
                </Button>
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        filteredStudents.length > 0 &&
                        selectedStudents.length === filteredStudents.length &&
                        filteredStudents.every((student) => selectedStudents.includes(student._id))
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Admission Number</TableHead>
                  <TableHead>Contact</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStudents.slice((page - 1) * itemsPerPage, page * itemsPerPage).map((student) => (
                  <TableRow key={student._id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedStudents.includes(student._id)}
                        onCheckedChange={() => handleStudentSelect(student._id)}
                      />
                    </TableCell>
                    <TableCell>{getFullName(student)}</TableCell>
                    <TableCell>{student.admissionNumber}</TableCell>
                    <TableCell>{student.phoneNumber}</TableCell>
                  </TableRow>
                ))}
                {filteredStudents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center">
                      {searchQuery
                        ? "No students found matching your search"
                        : "No unassigned students found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <Pagination
            currentPage={page}
            totalItems={filteredStudents.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setPage}
            onItemsPerPageChange={(newLimit) => { setItemsPerPage(newLimit); setPage(1); }}
            itemLabel="students"
          />
        </CardContent>
      </Card>
    </div>
  );
}

export default ClassTeacherStudentManagement;